<template>
  <div>
    <CAlert v-if="message" :color="messageColor" close-button @update:show="message = ''">{{ message }}</CAlert>
    <CCard>
      <CCardHeader class="d-flex justify-content-between align-items-center">
        <strong>Certificates &amp; Document Numbers</strong>
        <CButton color="primary" size="sm" to="/certificate/create">Create New</CButton>
      </CCardHeader>
      <CCardBody>
        <CRow>
          <CCol md="3"><CInput v-model="filters.search" label="Search number" @keyup.enter="load" /></CCol>
          <CCol md="2">
            <label>Year</label>
            <select v-model="filters.year" class="form-control"><option value="">All</option><option v-for="year in years" :key="year" :value="year">{{ year }}</option></select>
          </CCol>
          <CCol md="3">
            <label>Document Type</label>
            <select v-model="filters.document_type_id" class="form-control"><option value="">All</option><option v-for="item in meta.document_types" :key="item.id" :value="item.id">{{ item.name }} ({{ item.code }})</option></select>
          </CCol>
          <CCol md="2">
            <label>Status</label>
            <select v-model="filters.status" class="form-control"><option value="">All</option><option value="ACTIVE">Active</option><option value="CANCELLED">Cancelled</option></select>
          </CCol>
          <CCol md="2" class="d-flex align-items-end"><CButton color="info" block :disabled="loading" @click="load">{{ loading ? 'Loading...' : 'Apply Filters' }}</CButton></CCol>
        </CRow>
        <div class="table-responsive mt-3">
          <table class="table table-hover table-outline">
            <thead><tr><th>Number</th><th>Date</th><th>Type</th><th>Pillar / Code</th><th>People</th><th>Status</th><th>Created By</th><th class="text-right">Actions</th></tr></thead>
            <tbody>
              <tr v-for="item in rows" :key="item.uuid">
                <td><strong>{{ item.formatted_number }}</strong><div><CBadge :color="item.output_mode === 'CERTIFICATE' ? 'info' : 'secondary'">{{ item.output_mode }}</CBadge></div></td>
                <td>{{ item.document_date }}</td>
                <td>{{ item.document_type_name }} ({{ item.document_type_code }})<small v-if="variantName(item)" class="d-block text-muted">Varian: {{ variantName(item) }}</small></td>
                <td>{{ item.pillar_name }} ({{ item.pillar_code }})<br><small>{{ item.document_code_name }} ({{ item.document_code }})</small></td>
                <td>
                  <template v-if="item.people_expected_count">
                    <CBadge :color="item.people_count >= item.people_expected_count ? 'success' : 'warning'">{{ item.people_count }} / {{ item.people_expected_count }}</CBadge>
                    <div><a href="#" @click.prevent="openLinks(item)"><small>manage</small></a></div>
                  </template>
                  <small v-else class="text-muted">—</small>
                </td>
                <td><CBadge :color="item.status === 'ACTIVE' ? 'success' : 'danger'">{{ item.status }}</CBadge><div v-if="item.output_mode === 'CERTIFICATE'"><small>{{ item.generation_status }}</small></div></td>
                <td>{{ item.created_by }}</td>
                <td class="text-right text-nowrap">
                  <CButton size="sm" color="info" variant="outline" class="mr-1" @click="copy(item.formatted_number)">Copy</CButton>
                  <CButton v-if="item.generation_status === 'READY'" size="sm" color="primary" variant="outline" class="mr-1" @click="download(item)">PDF</CButton>
                  <CButton v-if="item.generation_status === 'READY'" size="sm" color="primary" variant="outline" class="mr-1" @click="openPdf(item)">Print</CButton>
                  <CButton v-if="item.generation_status === 'FAILED' && item.status === 'ACTIVE'" size="sm" color="warning" variant="outline" class="mr-1" @click="retry(item)">Retry</CButton>
                  <CButton v-if="item.status === 'ACTIVE'" size="sm" color="danger" variant="outline" @click="openCancel(item)">Cancel</CButton>
                </td>
              </tr>
              <tr v-if="!loading && !rows.length"><td colspan="8" class="text-center text-muted py-4">No records found.</td></tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted">{{ pagination.total }} records</small>
          <div><CButton size="sm" variant="outline" color="secondary" class="mr-2" :disabled="pagination.current_page <= 1" @click="changePage(pagination.current_page - 1)">Previous</CButton><span>Page {{ pagination.current_page }} / {{ pagination.last_page }}</span><CButton size="sm" variant="outline" color="secondary" class="ml-2" :disabled="pagination.current_page >= pagination.last_page" @click="changePage(pagination.current_page + 1)">Next</CButton></div>
        </div>
      </CCardBody>
    </CCard>
    <CModal title="Cancel Certificate" :show.sync="cancelModal">
      <p v-if="selected">Number <strong>{{ selected.formatted_number }}</strong> remains reserved and visible.</p>
      <CTextarea v-model="cancelReason" label="Cancellation reason" rows="4" />
      <template #footer><CButton color="secondary" @click="cancelModal = false">Close</CButton><CButton color="danger" :disabled="!cancelReason || saving" @click="confirmCancel">Cancel Certificate</CButton></template>
    </CModal>

    <CModal title="Linked People" size="xl" :show.sync="linksModal">
      <div v-if="linksLoading" class="text-center text-muted py-4">Loading...</div>
      <div v-else-if="detail">
        <p class="text-muted">Certificate <strong>{{ detail.formatted_number }}</strong><span v-if="variantName(detail)"> · Varian: {{ variantName(detail) }}</span></p>
        <div class="table-responsive">
          <table class="table table-sm">
            <thead><tr><th>Field</th><th>Person</th><th>Printed name</th><th>Source</th><th></th></tr></thead>
            <tbody>
              <tr v-for="slot in linkSlots" :key="slot.field.field_key">
                <td>{{ slot.field.label }}<br><CBadge color="info">{{ slot.field.people_role || 'subject' }}</CBadge></td>
                <td>
                  <template v-if="slot.link">{{ slot.link.people_name_at_link }}<br><small class="text-muted">{{ slot.link.people_uuid }}</small></template>
                  <template v-else-if="linkDraft[slot.field.field_key]">
                    {{ linkDraft[slot.field.field_key].name }}
                    <small class="text-muted d-block">{{ linkDraft[slot.field.field_key].email || 'no email' }}</small>
                    <a href="#" class="small" @click.prevent="openPeopleSearch(slot.field)">change</a>
                  </template>
                  <CButton v-else size="sm" color="primary" variant="outline" @click="openPeopleSearch(slot.field)">Choose People</CButton>
                </td>
                <td>{{ slot.link ? slot.link.name_snapshot : printedValue(slot.field.field_key) }}</td>
                <td><CBadge v-if="slot.link" :color="slot.link.link_source === 'manual' ? 'warning' : 'secondary'">{{ slot.link.link_source }}</CBadge></td>
                <td class="text-right text-nowrap">
                  <CButton v-if="!slot.link" size="sm" color="success" variant="outline" :disabled="!linkDraft[slot.field.field_key] || saving" @click="attach(slot.field)">Link</CButton>
                  <CButton v-else-if="slot.link.link_source === 'manual'" size="sm" color="danger" variant="outline" :disabled="saving" @click="detach(slot.link)">Remove</CButton>
                </td>
              </tr>
              <tr v-if="!linkSlots.length"><td colspan="5" class="text-center text-muted py-3">This document type has no people fields.</td></tr>
            </tbody>
          </table>
        </div>
        <small class="text-muted">Links created at issue time cannot be removed. Only manual links can.</small>
      </div>
      <template #footer><CButton color="secondary" @click="linksModal = false">Close</CButton></template>
    </CModal>

    <CertificatePeopleSearchModal
      :show.sync="peopleSearchModal"
      :exclude-uuids="draftExcludeUuids(peopleSearchField ? peopleSearchField.field_key : '')"
      @select="person => peopleSearchField && $set(linkDraft, peopleSearchField.field_key, person)"
    />
  </div>
</template>

<script>
import certificate from '@/services/certificate'
import CertificatePeopleSearchModal from '@/components/certificate/CertificatePeopleSearchModal'

export default {
  name: 'CertificateList',
  components: { CertificatePeopleSearchModal },
  data () {
    return {
      loading: false, saving: false, rows: [], meta: { document_types: [] }, message: '', messageColor: 'success',
      filters: { search: '', year: new Date().getFullYear(), document_type_id: '', status: '', page: 1, per_page: 25 },
      pagination: { current_page: 1, last_page: 1, total: 0 }, cancelModal: false, cancelReason: '', selected: null,
      linksModal: false, linksLoading: false, detail: null, linkDraft: {}, peopleSearchModal: false, peopleSearchField: null
    }
  },
  computed: {
    years () { const current = new Date().getFullYear(); return Array.from({ length: 8 }, (_, index) => current - index) },
    linkSlots () {
      if (!this.detail || !this.detail.document_type) return []
      const links = this.detail.people || []
      return (this.detail.document_type.fields || [])
        .filter(field => field.is_active && field.source_type === 'people')
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(field => ({ field, link: links.find(link => link.field_key === field.field_key) || null }))
    }
  },
  created () { this.initialize() },
  methods: {
    // Only multi-template types (e.g. GK iGrow Kelas 4-6 / 7-9) show which template was printed.
    variantName (item) { return item && item.template && item.document_type && item.document_type.allow_multiple_templates ? item.template.name : '' },
    async initialize () { try { const result = await certificate.getMeta(); this.meta = result.data; await this.load() } catch (error) { this.showError(error) } },
    async load () { this.loading = true; try { const result = await certificate.getCertificates(this.filters); this.rows = result.data.data; this.pagination = result.data } catch (error) { this.showError(error) } finally { this.loading = false } },
    changePage (page) { this.filters.page = page; this.load() },
    copy (number) { this.$copyText(number); this.message = 'Number copied.'; this.messageColor = 'success' },
    async download (item) { try { const response = await certificate.download(item.uuid); const url = URL.createObjectURL(response.data); const anchor = document.createElement('a'); anchor.href = url; anchor.download = `certificate-${item.formatted_number.replace(/\//g, '-')}.pdf`; anchor.click(); URL.revokeObjectURL(url) } catch (error) { this.showError(error) } },
    async openPdf (item) { const popup = window.open('', '_blank'); try { const response = await certificate.download(item.uuid); const url = URL.createObjectURL(response.data); popup.location = url } catch (error) { if (popup) popup.close(); this.showError(error) } },
    async retry (item) { try { const result = await certificate.retry(item.uuid); this.message = result.message; this.messageColor = result.data.generation_status === 'READY' ? 'success' : 'warning'; await this.load() } catch (error) { this.showError(error) } },
    async openLinks (item) { this.selected = item; this.linkDraft = {}; this.detail = null; this.linksModal = true; this.linksLoading = true; try { const result = await certificate.getCertificate(item.uuid); this.detail = result.data } catch (error) { this.showError(error) } finally { this.linksLoading = false } },
    openPeopleSearch (field) { this.peopleSearchField = field; this.peopleSearchModal = true },
    printedValue (fieldKey) { return (this.detail && this.detail.field_values && this.detail.field_values[fieldKey]) || '' },
    draftExcludeUuids (fieldKey) {
      const linked = ((this.detail && this.detail.people) || []).map(link => link.people_uuid)
      const drafted = Object.keys(this.linkDraft).filter(key => key !== fieldKey && this.linkDraft[key]).map(key => this.linkDraft[key].uuid)
      return linked.concat(drafted)
    },
    async attach (field) {
      this.saving = true
      try {
        const person = this.linkDraft[field.field_key]
        const result = await certificate.attachPeople(this.selected.uuid, { field_key: field.field_key, people_uuid: person.uuid })
        this.message = result.message; this.messageColor = 'success'
        this.$delete(this.linkDraft, field.field_key)
        await this.openLinks(this.selected)
        await this.load()
      } catch (error) { this.showError(error) } finally { this.saving = false }
    },
    async detach (link) {
      this.saving = true
      try {
        const result = await certificate.detachPeople(this.selected.uuid, link.id)
        this.message = result.message; this.messageColor = 'success'
        await this.openLinks(this.selected)
        await this.load()
      } catch (error) { this.showError(error) } finally { this.saving = false }
    },
    openCancel (item) { this.selected = item; this.cancelReason = ''; this.cancelModal = true },
    async confirmCancel () { this.saving = true; try { const result = await certificate.cancel(this.selected.uuid, this.cancelReason); this.message = result.message; this.messageColor = 'success'; this.cancelModal = false; await this.load() } catch (error) { this.showError(error) } finally { this.saving = false } },
    showError (error) { const data = error.response && error.response.data; this.message = (data && data.errors && Object.values(data.errors)[0][0]) || (data && data.message) || error.message || 'Request failed.'; this.messageColor = 'danger' }
  }
}
</script>
